import { FormEvent, useEffect, useMemo, useState } from 'react'
import type { ApiError, Behaviour, OverrideLevel, OverrideLimits, OverrideWindow, TariffSummary } from '../api/types'
import {
  ADVANCED_OPTIONS, BEHAVIOUR_TEXT, HALF_HOURS, PRICE_OPTIONS,
  checkDraft, floorHalfHour, ukDateString, ukToUtc,
} from '../lib/schedule'

export interface Draft {
  start: Date | null
  end: Date | null
  behaviour: Behaviour | null
}

interface Props {
  level: OverrideLevel
  limits: OverrideLimits
  tariff: TariffSummary | null
  windows: OverrideWindow[]
  disabled: boolean
  explanation: string | null
  onDraftChange: (d: Draft) => void
  onSubmit: (d: { start: Date; end: Date; behaviour: Behaviour; price: number | null }) => Promise<void>
}

function initialTimes() {
  const next = floorHalfHour(Date.now()) + 30 * 60_000
  const end = next + 60 * 60_000
  const hhmm = (ms: number) => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(ms))
  return { startDate: ukDateString(next), startTime: hhmm(next), endDate: ukDateString(end), endTime: hhmm(end) }
}

/** Add-window form: dates and half-hour times in UK time, an option, and a price where it applies. */
export function AddWindowForm({ level, limits, tariff, windows, disabled, explanation, onDraftChange, onSubmit }: Props) {
  const init = useMemo(initialTimes, [])
  const [startDate, setStartDate] = useState(init.startDate)
  const [startTime, setStartTime] = useState(init.startTime)
  const [endDate, setEndDate] = useState(init.endDate)
  const [endTime, setEndTime] = useState(init.endTime)
  const [behaviour, setBehaviour] = useState<Behaviour | null>(null)
  const [advanced, setAdvanced] = useState(false)
  const [price, setPrice] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const start = ukToUtc(startDate, startTime)
  const end = ukToUtc(endDate, endTime)
  const startMs = start?.getTime() ?? null
  const endMs = end?.getTime() ?? null

  useEffect(() => {
    onDraftChange({
      start: startMs != null ? new Date(startMs) : null,
      end: endMs != null ? new Date(endMs) : null,
      behaviour,
    })
  }, [startMs, endMs, behaviour, onDraftChange])

  const choose = (b: Behaviour) => {
    setBehaviour(b)
    setServerError(null)
    setDone(null)
    // Cheaper electricity needs a price: start from the off-peak rate.
    if (b === 'CHEAP' && price.trim() === '' && tariff?.off_peak_rate_p != null) {
      setPrice(String(tariff.off_peak_rate_p))
    }
    // An advanced option's price is optional; left blank it is stored as not given.
    if (ADVANCED_OPTIONS.includes(b) && behaviour === 'CHEAP') setPrice('')
  }

  const problem = checkDraft(start, end, behaviour, price, limits, windows, Date.now())
  const today = ukDateString(Date.now())
  const lastDay = ukDateString(Date.now() + limits.horizon_days * 86_400_000)
  const options = advanced && level === 'ALL' ? [...PRICE_OPTIONS, ...ADVANCED_OPTIONS] : PRICE_OPTIONS
  const showPrice = behaviour !== null && behaviour !== 'FREE'

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (problem || !start || !end || !behaviour) return
    setSubmitting(true)
    setServerError(null)
    setDone(null)
    try {
      const p = behaviour === 'FREE' || price.trim() === '' ? null : Number(price)
      await onSubmit({ start, end, behaviour, price: p })
      setDone('Window requested. It will show as Scheduled once your system confirms it.')
      setBehaviour(null)
      setPrice('')
    } catch (err) {
      const apiErr = err as ApiError
      setServerError(apiErr.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="card add-window" onSubmit={submit}>
      <h2>Add a window</h2>
      <fieldset disabled={disabled || submitting}>
        <div className="time-row">
          <label>
            Starts
            <input type="date" value={startDate} min={today} max={lastDay}
              onChange={(e) => { setStartDate(e.target.value); if (e.target.value > endDate) setEndDate(e.target.value) }} />
          </label>
          <label className="time-select">
            <span className="visually-hidden">Start time</span>
            <select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
              {HALF_HOURS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <div className="time-row">
          <label>
            Ends
            <input type="date" value={endDate} min={startDate} max={lastDay}
              onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <label className="time-select">
            <span className="visually-hidden">End time</span>
            <select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
              {HALF_HOURS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <p className="field-help muted">UK time. Up to {limits.max_hours} hours long, starting within the next {limits.horizon_days} days.</p>

        <div className="behaviour-options" role="radiogroup" aria-label="What should the battery do?">
          {options.map((b) => (
            <label key={b} className={`behaviour-option${behaviour === b ? ' selected' : ''}`}>
              <input type="radio" name="behaviour" value={b} checked={behaviour === b} onChange={() => choose(b)} />
              <span className="behaviour-label">{BEHAVIOUR_TEXT[b].label}</span>
              <span className="behaviour-explain">{BEHAVIOUR_TEXT[b].explain}</span>
            </label>
          ))}
        </div>

        {level === 'ALL' && (
          <label className="advanced-toggle">
            <input type="checkbox" checked={advanced}
              onChange={(e) => {
                setAdvanced(e.target.checked)
                if (!e.target.checked && behaviour && ADVANCED_OPTIONS.includes(behaviour)) setBehaviour(null)
              }} />
            Advanced — choose exactly what the battery does
          </label>
        )}

        {showPrice && (
          <label className="price-field">
            Price in p/kWh including VAT{behaviour !== 'CHEAP' && ' (optional)'}
            <input
              type="text" inputMode="decimal" value={price}
              placeholder={tariff?.off_peak_rate_p != null ? `e.g. ${tariff.off_peak_rate_p}` : 'e.g. 7.5'}
              onChange={(e) => setPrice(e.target.value)}
            />
            <span className="field-help muted">
              {behaviour === 'CHEAP'
                ? 'What your supplier charges during this window. It is kept for your savings statement.'
                : 'Only if you know it. It is kept for your savings statement.'}
            </span>
          </label>
        )}

        {behaviour && explanation && <p className="preview-explain">{explanation}</p>}
        {behaviour && problem && <p className="form-problem">{problem}</p>}
        {serverError && <p className="form-problem" role="alert">{serverError}</p>}
        {done && <p className="form-done" role="status">{done}</p>}

        <button type="submit" disabled={!!problem || !behaviour}>
          {submitting ? 'Sending…' : 'Add window'}
        </button>
      </fieldset>
    </form>
  )
}
